import { Check, Eye, EyeOff, RefreshCw, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { SCOPE_RANK, fieldScope } from "@/lib/binding-scope";
import { cn } from "@/lib/utils";
import type {
  BindingScope,
  ProviderEnumSource,
  ProviderField,
  ProviderFieldSchema,
  ProvenanceSource,
} from "@tilt/admin-sdk";

/** Format an inherited value for the read-only display row.
 *  Primitives render as-is; arrays of primitives as "a, b"; anything
 *  structured is JSON-stringified and truncated so we never show the
 *  dreaded [object Object]. */
function renderInheritedValue(value: unknown): string {
  const MAX = 140;
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    const primitive = value.every(
      (v) => typeof v === "string" || typeof v === "number" || typeof v === "boolean",
    );
    if (primitive) return (value as unknown[]).join(", ");
  }
  let stringified: string;
  try {
    stringified = JSON.stringify(value);
  } catch {
    return "(unserialisable)";
  }
  return stringified.length > MAX ? stringified.slice(0, MAX - 1) + "…" : stringified;
}

/**
 * Schema-driven form that renders one typed input per property in a
 * ProviderFieldSchema. Helpers (hasProperties, buildValuesFromSchema)
 * and pure types live in @/lib/schema-form so Vite's react-refresh
 * is happy (components-only file).
 *
 * Supported field types: `string` (+ format email/phone/url), `number`,
 * `boolean`, `time` (HH:MM), `enum` (dropdown sourced from inline
 * options OR a remote endpoint), `array<enum>` (multi-select chips
 * sourced same way). Secret strings render as password + reveal-eye.
 *
 * Remote enum sources: caller supplies an `enumResolver` that knows
 * how to translate a ProviderEnumSource + the current values bag into
 * a list of options. Typical implementation hits an admin-api
 * passthrough endpoint like `/admin/v1/providers/valor/merchants`.
 * The resolver runs on mount + on dependency change + on the user
 * clicking the Refresh icon next to the field.
 */

export type EnumOption = { value: string; label: string };

/** Caller-provided fetcher for remote enum sources. Accepts the
 *  ProviderEnumSource + the current form values (so depends_on
 *  siblings can be substituted into the endpoint), returns options. */
export type EnumResolver = (
  source: ProviderEnumSource,
  values: Record<string, unknown>,
) => Promise<EnumOption[]>;

export interface SchemaFormProps {
  schema: ProviderFieldSchema;
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** Optional intro caption - shown above the first field. */
  caption?: string;
  /** Extra classes for the outer container. */
  className?: string;
  /** Required if any field declares `type: "enum" | "array"` with a
   *  `source.endpoint`. Pure inline-options enums don't need this. */
  enumResolver?: EnumResolver;
  /**
   * Ambient form values from outside the schema. Used by ``visible_when``
   * rules on individual fields - e.g. the BindingDialog passes
   * ``{feature_path}`` so a provider schema can gate
   * ``card_present_devices`` on ``feature_path=="pos.payments.card_present"``.
   * Ambient values are merged with in-schema values at rule evaluation
   * time; in-schema values win on key collision.
   */
  ambient?: Record<string, unknown>;
  /**
   * Push 13: when set, fields whose ``x-scope`` annotation is more
   * specific than ``scopeFilter`` are hidden; fields whose ``x-scope``
   * is less specific are rendered read-only with an "inherited from
   * {scope}" pill (provided ``inheritedValues`` supplies the effective
   * value to show).
   *
   * Leaving this undefined preserves the pre-push-13 "render every
   * field" behaviour - used by the credential dialog and any other
   * single-tier form.
   */
  scopeFilter?: BindingScope;
  /**
   * Push 13: effective values + their source scope for fields that
   * SchemaForm should surface read-only (inherited from an ancestor).
   * Typically sourced from ``ResolvedBinding.provenance`` + the
   * merged ``effective.config``.
   */
  inheritedValues?: Record<string, { value: unknown; scope: ProvenanceSource }> | undefined;
}

/**
 * Is this field visible given the current ambient + in-schema values?
 * Fields without a ``visible_when`` always pass (pre-push-10 behaviour).
 * A rule matches when the referenced field's value is in ``rule.in``.
 */
function isFieldVisible(
  field: ProviderField,
  values: Record<string, unknown>,
  ambient: Record<string, unknown> | undefined,
): boolean {
  const rule = field.visible_when;
  if (!rule) return true;
  const merged = ambient ? { ...ambient, ...values } : values;
  const v = merged[rule.field];
  if (typeof v !== "string") return false;
  return rule.in.includes(v);
}

export function SchemaForm(props: SchemaFormProps): JSX.Element {
  const { schema, values, onChange, caption, className, enumResolver, ambient } = props;
  const { scopeFilter, inheritedValues } = props;
  const allEntries = Object.entries(schema.properties ?? {}).filter(([, field]) =>
    isFieldVisible(field, values, ambient),
  );

  // Push 13 split:
  //   * editable - field's x-scope matches the filter (or no filter).
  //   * inherited - field's x-scope is less specific than the filter;
  //     rendered read-only in the "Inherited" block at the top. When
  //     the ancestry didn't set a value, the row still renders and
  //     shows "(not set at any parent scope)" so admins can see what
  //     fields *could* be inherited - just flagged as empty.
  //   * hidden - field's x-scope is more specific than the filter
  //     (e.g. merchant_id on a partner form); not rendered at all.
  const entries: Array<[string, ProviderField]> = [];
  const inherited: Array<[string, ProviderField]> = [];
  for (const [key, field] of allEntries) {
    if (!scopeFilter) {
      entries.push([key, field]);
      continue;
    }
    const fs = fieldScope(field);
    const fr = SCOPE_RANK[fs];
    const tr = SCOPE_RANK[scopeFilter];
    if (fr === tr) entries.push([key, field]);
    else if (fr < tr) inherited.push([key, field]);
    // fr > tr → hidden (don't render)
  }
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const setField = (key: string, value: unknown): void => {
    onChange({ ...values, [key]: value });
  };
  return (
    <div className={cn("space-y-3 rounded-md border bg-muted/20 p-3", className)}>
      {caption && <p className="text-[11px] text-muted-foreground">{caption}</p>}
      {inherited.length > 0 && (
        <div className="rounded-md border border-dashed bg-background p-2">
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Inherited - edit at the parent scope to change
          </p>
          <ul className="space-y-1">
            {inherited.map(([key, field]) => {
              const inh = inheritedValues?.[key];
              const display =
                inh == null
                  ? "(not set at any parent scope)"
                  : inh.value == null || inh.value === ""
                    ? "(empty)"
                    : renderInheritedValue(inh.value);
              return (
                <li key={key} className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">{field.title ?? key}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-[11px]">{display}</span>
                    {inh && (
                      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                        {inh.scope === "catalog-default" ? "catalog default" : `from ${inh.scope}`}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {entries.length === 0 && inherited.length === 0 && (
        <p className="text-[11px] text-muted-foreground">
          This provider has no configurable fields - submit as-is.
        </p>
      )}
      {entries.map(([key, field]) => {
        const labelText = `${field.title ?? key}${field.required ? " *" : ""}`;
        const current = values[key];

        if (field.type === "boolean") {
          return (
            <label key={key} className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={Boolean(current ?? field.default ?? false)}
                onChange={(e) => setField(key, e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 accent-primary"
              />
              <span>
                <span className="font-medium">{labelText}</span>
                {field.hint && (
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    {field.hint}
                  </span>
                )}
              </span>
            </label>
          );
        }

        if (field.type === "number") {
          return (
            <LabeledField key={key} label={labelText} hint={field.hint}>
              <input
                type="number"
                inputMode="decimal"
                required={field.required}
                value={current == null ? "" : String(current)}
                onChange={(e) => {
                  const v = e.target.value;
                  setField(key, v === "" ? "" : Number(v));
                }}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder={field.default !== undefined ? String(field.default) : undefined}
              />
            </LabeledField>
          );
        }

        if (field.type === "time") {
          // HH:MM 24h picker. Default values render as placeholders,
          // not actual values, so the user sees "00:00" without it
          // having been saved.
          return (
            <LabeledField key={key} label={labelText} hint={field.hint}>
              <input
                type="time"
                required={field.required}
                value={current == null ? "" : String(current)}
                onChange={(e) => setField(key, e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
                placeholder={field.default !== undefined ? String(field.default) : undefined}
              />
            </LabeledField>
          );
        }

        if (field.type === "enum") {
          return (
            <EnumField
              key={key}
              fieldKey={key}
              label={labelText}
              field={field}
              value={current}
              values={values}
              onChange={(v) => setField(key, v)}
              enumResolver={enumResolver}
            />
          );
        }

        if (field.type === "array") {
          return (
            <ArrayField
              key={key}
              fieldKey={key}
              label={labelText}
              field={field}
              value={Array.isArray(current) ? (current as string[]) : []}
              values={values}
              onChange={(v) => setField(key, v)}
              enumResolver={enumResolver}
            />
          );
        }

        // default → string (incl. email/phone/url/secret)
        const isSecret = Boolean(field.secret);
        const show = revealed[key] === true;
        const inputType =
          isSecret && !show
            ? "password"
            : field.format === "email"
              ? "email"
              : field.format === "phone"
                ? "tel"
                : field.format === "url"
                  ? "url"
                  : "text";
        return (
          <LabeledField key={key} label={labelText} hint={field.hint}>
            <div className="flex items-stretch gap-2">
              <input
                type={inputType}
                required={field.required}
                spellCheck={false}
                autoComplete={isSecret ? "new-password" : undefined}
                value={current == null ? "" : String(current)}
                onChange={(e) => setField(key, e.target.value)}
                className={cn(
                  "w-full rounded-md border bg-background px-3 py-2 text-sm",
                  isSecret && "font-mono",
                )}
                placeholder={
                  field.default !== undefined
                    ? String(field.default)
                    : isSecret
                      ? "(will be encrypted at rest)"
                      : undefined
                }
              />
              {isSecret && (
                <button
                  type="button"
                  onClick={() => setRevealed({ ...revealed, [key]: !show })}
                  aria-label={show ? "Hide value" : "Show value"}
                  className="flex items-center justify-center rounded-md border px-2 text-muted-foreground hover:bg-muted"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
          </LabeledField>
        );
      })}
    </div>
  );
}

// --------------------------------------------------------------------- //
// Enum + array<enum> - share the same options-fetching hook
// --------------------------------------------------------------------- //

function useEnumOptions(
  field: ProviderField,
  values: Record<string, unknown>,
  enumResolver: EnumResolver | undefined,
): {
  options: EnumOption[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  blockedBy: string[];
} {
  const source = field.source;
  const [options, setOptions] = useState<EnumOption[]>(source?.options ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  // Wrap depends_on in its own useMemo so the downstream useMemo deps
  // don't change every render from `?? []` creating a fresh array.
  const deps = useMemo(() => source?.depends_on ?? [], [source?.depends_on]);

  // Gate remote fetches on all dependencies having values. Without
  // this Valor's /devices call would fire with merchant_id=:merchant_id
  // as a literal.
  const blockedBy = useMemo(
    () => deps.filter((d) => values[d] == null || values[d] === ""),
    [deps, values],
  );

  // Stable key for depends_on values to use as effect dep.
  const depsKey = useMemo(
    () => deps.map((d) => `${d}=${String(values[d] ?? "")}`).join("|"),
    [deps, values],
  );

  useEffect(() => {
    // Inline-options mode - just sync when the schema changes.
    if (!source?.endpoint) {
      setOptions(source?.options ?? []);
      setLoading(false);
      setError(null);
      return;
    }
    if (!enumResolver) {
      setOptions([]);
      setError("No enum resolver configured for this form.");
      return;
    }
    if (blockedBy.length > 0) {
      setOptions([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    enumResolver(source, values)
      .then((opts) => {
        if (cancelled) return;
        setOptions(opts);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load options");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.endpoint, depsKey, nonce, enumResolver, blockedBy.length]);

  return {
    options,
    loading,
    error,
    refresh: () => setNonce((n) => n + 1),
    blockedBy,
  };
}

function EnumField(props: {
  fieldKey: string;
  label: string;
  field: ProviderField;
  value: unknown;
  values: Record<string, unknown>;
  onChange: (v: string) => void;
  enumResolver?: EnumResolver | undefined;
}): JSX.Element {
  const { field, value, onChange } = props;
  const { options, loading, error, refresh, blockedBy } = useEnumOptions(
    field,
    props.values,
    props.enumResolver,
  );
  const remote = Boolean(field.source?.endpoint);
  return (
    <LabeledField label={props.label} hint={field.hint}>
      <div className="flex items-stretch gap-2">
        <select
          required={field.required}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={loading || blockedBy.length > 0}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
        >
          <option value="">
            {blockedBy.length > 0
              ? `Pick ${blockedBy.join(", ")} first…`
              : loading
                ? "Loading…"
                : options.length === 0
                  ? "(no options)"
                  : "Select…"}
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {remote && (
          <button
            type="button"
            onClick={refresh}
            disabled={loading || blockedBy.length > 0}
            aria-label="Refresh options"
            title="Re-fetch from the vendor API"
            className="flex items-center justify-center rounded-md border px-2 text-muted-foreground hover:bg-muted disabled:opacity-40"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </button>
        )}
      </div>
      {error && <p className="mt-1 text-[11px] text-destructive">{error}</p>}
    </LabeledField>
  );
}

function ArrayField(props: {
  fieldKey: string;
  label: string;
  field: ProviderField;
  value: string[];
  values: Record<string, unknown>;
  onChange: (v: string[]) => void;
  enumResolver?: EnumResolver | undefined;
}): JSX.Element {
  const { field, value, onChange } = props;
  const { options, loading, error, refresh, blockedBy } = useEnumOptions(
    field,
    props.values,
    props.enumResolver,
  );
  const remote = Boolean(field.source?.endpoint);
  const toggle = (v: string): void => {
    const set = new Set(value);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    onChange(Array.from(set));
  };
  const valueLookup = new Map(options.map((o) => [o.value, o.label]));
  // Orphans = saved values that no longer appear in the resolved
  // options list (e.g. Valor deactivated a device on their side).
  // Pre-push-10 we rendered a chip for EVERY selected value, so an
  // in-options selection showed up twice - once as a chip, once as a
  // ticked checkbox below. Chips now cover orphans only; the checkbox
  // row handles the common in-options case.
  const orphans = value.filter((v) => !valueLookup.has(v));

  return (
    <LabeledField label={props.label} hint={field.hint}>
      <div className="rounded-md border bg-background p-2">
        {orphans.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            {orphans.map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-100/40 px-2 py-0.5 text-[11px] text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
                title="This value is no longer in the provider's options list - probably removed on the vendor side. Click × to detach it."
              >
                {v}
                <button
                  type="button"
                  onClick={() => toggle(v)}
                  aria-label={`Remove orphaned value ${v}`}
                  className="rounded hover:bg-amber-200/40"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {blockedBy.length > 0 && (
          <p className="text-[11px] text-muted-foreground">
            Pick {blockedBy.join(", ")} first to load options.
          </p>
        )}

        {blockedBy.length === 0 && (
          <div className="max-h-40 overflow-y-auto">
            {loading && <p className="text-[11px] text-muted-foreground">Loading options…</p>}
            {!loading && options.length === 0 && (
              <p className="text-[11px] text-muted-foreground">(no options)</p>
            )}
            <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {options.map((o) => {
                const on = value.includes(o.value);
                return (
                  <li key={o.value}>
                    <label
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1 text-xs",
                        on ? "border-primary/40 bg-primary/5" : "bg-background",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(o.value)}
                        className="h-3.5 w-3.5 accent-primary"
                      />
                      <span>{o.label}</span>
                      {on && <Check className="ml-auto h-3 w-3 text-primary" aria-hidden />}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {remote && (
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={refresh}
              disabled={loading || blockedBy.length > 0}
              aria-label="Refresh options"
              className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted disabled:opacity-40"
            >
              <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
              Refresh
            </button>
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-[11px] text-destructive">{error}</p>}
    </LabeledField>
  );
}

function LabeledField(props: {
  label: string;
  hint?: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{props.label}</span>
      {props.children}
      {props.hint && <p className="mt-1 text-[11px] text-muted-foreground">{props.hint}</p>}
    </label>
  );
}
